import * as React from "react";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronRight } from "lucide-react";

interface DesignLibrarySummaryProps {
  themeColors: {
    light: string;
    subtle: string;
    muted: string;
    dark: string;
    primary: string;
    secondary: string;
  };
  h1Typography?: {
    name?: string;
    size?: number;
    weight?: number;
  };
  bodyTypography?: {
    name?: string;
    size?: number;
    weight?: number;
  };
  onColorsClick?: () => void;
  onTypographyClick?: () => void;
  onCustomFontsClick?: () => void;
}

export const DesignLibrarySummary: React.FC<DesignLibrarySummaryProps> = ({
  themeColors,
  h1Typography,
  bodyTypography,
  onColorsClick,
  onTypographyClick,
  onCustomFontsClick,
}) => {
  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-lg border border-border bg-white shadow-sm">
      <div className="flex flex-1 p-6 flex-col items-start gap-6 self-stretch border-b-0.5 border-border">
        <div className="flex justify-between items-start self-stretch">
          <div className="font-semibold">Colors</div>
          <IconButton
            variant="secondary"
            size="sm"
            layoutClassName="h-3 w-3"
            icon={<ChevronRight size={12} className="text-default" />}
            onClick={onColorsClick}
          />
        </div>
        <div className="flex items-center self-stretch">
          {Object.entries(themeColors).map(([key, color], index) => (
            <div
              key={key}
              className={twMerge(
                "h-16 rounded-lg border-0.5 border-black/10",
                index > 0 ? "-ml-4" : "",
              )}
              style={{
                backgroundColor: color,
                flex: "1 0 0",
                alignSelf: "stretch",
              }}
            ></div>
          ))}
        </div>
      </div>
      <div className="flex flex-2 p-6 flex-col items-start gap-6 self-stretch border-b-0.5 border-border">
        <div className="flex justify-between items-start self-stretch">
          <div className="font-semibold">Typography</div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onCustomFontsClick}>
              Custom Fonts
            </Button>
            <IconButton
              variant="secondary"
              size="sm"
              layoutClassName="h-3 w-3"
              icon={<ChevronRight size={12} className="text-default" />}
              onClick={onTypographyClick}
            />
          </div>
        </div>
        <div className="flex flex-col p-3 justify-center items-center self-stretch rounded-lg bg-sheet">
          <div
            className="flex h-8 flex-col justify-center items-center self-stretch text-center"
            style={{
              fontFamily: h1Typography?.name
                ? `'${h1Typography.name}', sans-serif`
                : undefined,
              fontSize: "42px",
              fontWeight: h1Typography?.weight ?? 600,
            }}
          >
            Heading
          </div>
          <div
            style={{
              fontFamily: bodyTypography?.name
                ? `'${bodyTypography.name}', sans-serif`
                : undefined,
              fontSize: "16px",
              fontWeight: bodyTypography?.weight ?? 400,
            }}
          >
            This is your paragraph.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 self-stretch">
          <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
            <div className="text-sm font-medium text-muted">Headings</div>
            <div
              className="text-3xl font-semibold truncate min-w-0"
              style={{
                fontFamily: h1Typography?.name
                  ? `'${h1Typography.name}', sans-serif`
                  : undefined,
              }}
            >
              {h1Typography?.name ?? "Default"}
            </div>
          </div>
          <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
            <div className="text-sm font-medium text-muted">Body</div>
            <div
              className="text-3xl font-semibold truncate min-w-0"
              style={{
                fontFamily: bodyTypography?.name
                  ? `'${bodyTypography.name}', sans-serif`
                  : undefined,
              }}
            >
              {bodyTypography?.name ?? "Default"}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-1 p-6 flex-col items-start gap-6 self-stretch border-b-0.5 border-border">
        <div className="flex justify-between items-start self-stretch">
          <div className="font-semibold">Corner Rounding</div>
        </div>
        <div className="flex items-start gap-3 self-stretch">
          <div className="flex flex-col gap-2 p-2 justify-between items-center flex-1 self-stretch border-0.5 border-border rounded-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 21 21"
              fill="none"
            >
              <path d="M0.75 20.8V0.800049H20.75" stroke="#475569" />
            </svg>
            <div className="text-sm font-medium text-muted">None</div>
          </div>
          <div className="flex flex-col gap-2 p-2 justify-between items-center flex-1 self-stretch border-0.5 border-border rounded-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 21 21"
              fill="none"
            >
              <path
                d="M1.25 20.8V3.80005C1.25 2.14319 2.59315 0.800049 4.25 0.800049H21.25"
                stroke="#475569"
              />
            </svg>
            <div className="text-sm font-medium text-muted">Small</div>
          </div>
          <div className="flex flex-col gap-2 p-2 justify-between items-center flex-1 self-stretch border-0.5 border-border rounded-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 21 21"
              fill="none"
            >
              <path
                d="M0.75 20.8V6.80005C0.75 3.48634 3.43629 0.800049 6.75 0.800049H20.75"
                stroke="#475569"
              />
            </svg>
            <div className="text-sm font-medium text-muted">Medium</div>
          </div>
          <div className="flex flex-col gap-2 p-2 justify-between items-center flex-1 self-stretch border-0.5 border-border rounded-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 21 21"
              fill="none"
            >
              <path
                d="M1.25 20.8V8.80005C1.25 4.38177 4.83172 0.800049 9.25 0.800049H21.25"
                stroke="#475569"
              />
            </svg>
            <div className="text-sm font-medium text-muted">Large</div>
          </div>
        </div>
      </div>
    </div>
  );
};
